import { useMemo, useState, useCallback } from 'react';
import { Event } from '../types/event.types';

interface WeekRange {
  start: Date;
  end: Date;
  weekNumber: number;
}

export interface SmartRange {
  dayKeys: string[];
  label: string; // e.g. "Thursday 9 April" or "Mon 6 – Wed 8 April"
}

export interface UsePlanningViewReturn {
  selectedDate: Date;
  setSelectedDate: (date: Date) => void;
  goToNextDay: () => void;
  goToPreviousDay: () => void;
  goToNextWeek: () => void;
  goToPreviousWeek: () => void;
  goToToday: () => void;

  /** All events grouped by day for the current week (desktop grid) */
  eventsByDay: Map<string, Event[]>;
  /** Smart subset of days to show on mobile */
  smartRange: SmartRange;
  /** Events only for smart range days */
  smartRangeEventsByDay: Map<string, Event[]>;

  draftsWithoutDate: Event[];
  totalEventsThisWeek: number;

  currentWeek: WeekRange;

  isLoading: boolean;
}

/**
 * Get Monday 00:00 of the week containing the given date
 */
const getWeekStart = (date: Date): Date => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  return d;
};

/**
 * Get Sunday 23:59:59 of the week containing the given date
 */
const getWeekEnd = (date: Date): Date => {
  const start = getWeekStart(date);
  const end = new Date(start);
  end.setDate(end.getDate() + 6);
  end.setHours(23, 59, 59, 999);
  return end;
};

/**
 * Get ISO week number
 */
const getWeekNumber = (date: Date): number => {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
};

/**
 * Format date as YYYY-MM-DD key
 */
const toDayKey = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const DAY_NAMES_SHORT = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const DAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const getDowIndex = (date: Date): number => {
  const d = date.getDay();
  return d === 0 ? 6 : d - 1; // Mon=0, Sun=6
};

const formatDayLabel = (dateKey: string): string => {
  const d = new Date(dateKey + 'T00:00:00');
  const dow = DAY_NAMES[getDowIndex(d)];
  return `${dow} ${d.getDate()} ${MONTH_NAMES[d.getMonth()]}`;
};

const formatShortDayLabel = (dateKey: string): string => {
  const d = new Date(dateKey + 'T00:00:00');
  const dow = DAY_NAMES_SHORT[getDowIndex(d)];
  return `${dow} ${d.getDate()}`;
};

/** Min events to show on mobile before we stop adding days */
const MIN_EVENTS = 3;
/** Max events on mobile — if a single day has this many, show only that day */
const DENSE_DAY_THRESHOLD = 4;

/**
 * Compute smart range: which days to show on mobile starting from selectedDate.
 *
 * Rules:
 * - If selected day has DENSE_DAY_THRESHOLD+ events → show only that day
 * - Otherwise, accumulate days forward until we reach MIN_EVENTS total
 *   but never cut a day in half (always include all events from a day)
 * - If adding the next day would bring us to 7+ events AND we already have MIN_EVENTS,
 *   stop before adding it (unless it would be the only day)
 */
const MAX_SMART_RANGE_DAYS = 7; // max days to look ahead

const computeSmartRange = (
  selectedDateKey: string,
  allEventsByDay: Map<string, Event[]>,
): SmartRange => {
  const selectedCount = allEventsByDay.get(selectedDateKey)?.length || 0;

  // Dense day: show only this day
  if (selectedCount >= DENSE_DAY_THRESHOLD) {
    return {
      dayKeys: [selectedDateKey],
      label: formatDayLabel(selectedDateKey),
    };
  }

  // Accumulate from selected date forward (up to MAX_SMART_RANGE_DAYS, no week boundary)
  const dayKeys: string[] = [];
  let totalEvents = 0;
  const current = new Date(selectedDateKey + 'T00:00:00');
  let daysChecked = 0;

  while (daysChecked < MAX_SMART_RANGE_DAYS) {
    const key = toDayKey(current);
    const dayCount = allEventsByDay.get(key)?.length || 0;

    // If we already have enough events and adding this day would make it too dense
    if (dayKeys.length > 0 && totalEvents >= MIN_EVENTS && dayCount >= DENSE_DAY_THRESHOLD) {
      break;
    }

    dayKeys.push(key);
    totalEvents += dayCount;

    // If we've reached MIN_EVENTS and this day has events, we can stop
    if (totalEvents >= MIN_EVENTS && dayCount > 0) {
      break;
    }

    current.setDate(current.getDate() + 1);
    daysChecked++;
  }

  // Ensure at least the selected day is included
  if (dayKeys.length === 0) {
    dayKeys.push(selectedDateKey);
  }

  // Build label
  let label: string;
  if (dayKeys.length === 1) {
    label = formatDayLabel(dayKeys[0]);
  } else {
    const first = dayKeys[0];
    const last = dayKeys[dayKeys.length - 1];
    const firstDate = new Date(first + 'T00:00:00');
    const lastDate = new Date(last + 'T00:00:00');
    const sameMonth = firstDate.getMonth() === lastDate.getMonth();
    if (sameMonth) {
      label = `${formatShortDayLabel(first)} – ${formatShortDayLabel(last)} ${MONTH_NAMES[firstDate.getMonth()]}`;
    } else {
      label = `${formatShortDayLabel(first)} ${MONTH_NAMES[firstDate.getMonth()]} – ${formatShortDayLabel(last)} ${MONTH_NAMES[lastDate.getMonth()]}`;
    }
  }

  return { dayKeys, label };
};

/**
 * usePlanningView — Groups organizer events by day for the selected week
 */
export const usePlanningView = (
  events: Event[],
  isEventsLoading: boolean
): UsePlanningViewReturn => {
  const [selectedDate, setSelectedDate] = useState<Date>(() => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return now;
  });

  const goToNextDay = useCallback(() => {
    setSelectedDate(prev => {
      const next = new Date(prev);
      next.setDate(next.getDate() + 1);
      return next;
    });
  }, []);

  const goToPreviousDay = useCallback(() => {
    setSelectedDate(prev => {
      const prev2 = new Date(prev);
      prev2.setDate(prev2.getDate() - 1);
      return prev2;
    });
  }, []);

  const goToNextWeek = useCallback(() => {
    setSelectedDate(prev => {
      const next = new Date(prev);
      next.setDate(next.getDate() + 7);
      return next;
    });
  }, []);

  const goToPreviousWeek = useCallback(() => {
    setSelectedDate(prev => {
      const prev2 = new Date(prev);
      prev2.setDate(prev2.getDate() - 7);
      return prev2;
    });
  }, []);

  const goToToday = useCallback(() => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    setSelectedDate(now);
  }, []);

  const currentWeek = useMemo((): WeekRange => ({
    start: getWeekStart(selectedDate),
    end: getWeekEnd(selectedDate),
    weekNumber: getWeekNumber(selectedDate),
  }), [selectedDate]);

  const { eventsByDay, draftsWithoutDate, totalEventsThisWeek } = useMemo(() => {
    const byDay = new Map<string, Event[]>();
    const noDates: Event[] = [];
    let total = 0;

    const weekStart = currentWeek.start.getTime();
    const weekEnd = currentWeek.end.getTime();

    for (const event of events) {
      if (!event.datetime) {
        if (event.status === 'draft') {
          noDates.push(event);
        }
        continue;
      }

      const eventDate = new Date(event.datetime);
      const eventTime = eventDate.getTime();

      if (eventTime < weekStart || eventTime > weekEnd) continue;

      const key = toDayKey(eventDate);
      const existing = byDay.get(key);
      if (existing) {
        existing.push(event);
      } else {
        byDay.set(key, [event]);
      }
      total++;
    }

    // Sort events within each day by start time
    byDay.forEach((dayEvents: Event[]) => {
      dayEvents.sort((a: Event, b: Event) => {
        const aTime = a.datetime ? new Date(a.datetime).getTime() : 0;
        const bTime = b.datetime ? new Date(b.datetime).getTime() : 0;
        return aTime - bTime;
      });
    });

    return { eventsByDay: byDay, draftsWithoutDate: noDates, totalEventsThisWeek: total };
  }, [events, currentWeek]);

  // All events by day (no week filter) — used for smart range that crosses week boundaries
  const allEventsByDay = useMemo(() => {
    const byDay = new Map<string, Event[]>();
    for (const event of events) {
      if (!event.datetime) continue;
      const key = toDayKey(new Date(event.datetime));
      const existing = byDay.get(key);
      if (existing) {
        existing.push(event);
      } else {
        byDay.set(key, [event]);
      }
    }
    byDay.forEach((dayEvents: Event[]) => {
      dayEvents.sort((a: Event, b: Event) => {
        const aTime = a.datetime ? new Date(a.datetime).getTime() : 0;
        const bTime = b.datetime ? new Date(b.datetime).getTime() : 0;
        return aTime - bTime;
      });
    });
    return byDay;
  }, [events]);

  // Smart range for mobile — uses allEventsByDay (no week boundary)
  const selectedDateKey = toDayKey(selectedDate);

  const smartRange = useMemo(
    () => computeSmartRange(selectedDateKey, allEventsByDay),
    [selectedDateKey, allEventsByDay]
  );

  const smartRangeEventsByDay = useMemo(() => {
    const filtered = new Map<string, Event[]>();
    for (const key of smartRange.dayKeys) {
      const dayEvents = allEventsByDay.get(key);
      if (dayEvents && dayEvents.length > 0) {
        filtered.set(key, dayEvents);
      }
    }
    return filtered;
  }, [smartRange.dayKeys, allEventsByDay]);

  return {
    selectedDate,
    setSelectedDate,
    goToNextDay,
    goToPreviousDay,
    goToNextWeek,
    goToPreviousWeek,
    goToToday,
    eventsByDay,
    smartRange,
    smartRangeEventsByDay,
    draftsWithoutDate,
    totalEventsThisWeek,
    currentWeek,
    isLoading: isEventsLoading,
  };
};
